/**
 * services/auth/auth.ts
 * Platform auth/identity calls for SK Queue, via the Backbone hub. Cookies
 * (accessToken/refreshToken) are httpOnly — never touched here. We only read the
 * session (/v1/me) and clear it (/logout). Login itself happens on the
 * centralized platform auth page (see navigation/platformAuthRedirect).
 *
 * Mirrors catalog's auth service: a 5s /me cache + in-flight dedupe so the boot
 * probe and any parallel callers share one request.
 */

import axiosInstance from '../../api/axiosInstance';
import { Token_Handler } from '../../storage/tokenHandler';
import { Platform_Me_Url, Platform_Logout_Url, Platform_Switch_Org_Url } from '../../api/endpoints';

/** The platform mints product-scoped JWTs; SK Queue uses the 'queue' product so
 *  the issued token carries aud=queue (what sk-queue-api verifies). */
export const PRODUCT = 'queue' as const;

const ME_TTL = 5_000;
let _meCache: { data: any; at: number } | null = null;
let _meInflight: Promise<any> | null = null;

export const invalidateMeCache = () => { _meCache = null; };

/** GET /v1/me — identity probe. 200 = signed in, 401 = not. */
export const getCurrentUser = async (): Promise<any> => {
  if (_meCache && Date.now() - _meCache.at < ME_TTL) return _meCache.data;
  if (!_meInflight) {
    _meInflight = axiosInstance
      .get(Platform_Me_Url)
      .then((r) => { _meCache = { data: r.data, at: Date.now() }; return r.data; })
      .finally(() => { _meInflight = null; });
  }
  return _meInflight;
};

/** POST /v1/auth/logout — clears the session cookies. */
export const logoutUser = async (): Promise<void> => {
  try {
    await axiosInstance.post(Platform_Logout_Url);
  } finally {
    invalidateMeCache();
    await Token_Handler.clearAll();
  }
};

/** POST /v1/auth/switch-org — re-mint cookies scoped to another org. */
export const switchOrg = async (orgId: string): Promise<any> => {
  const { data } = await axiosInstance.post(Platform_Switch_Org_Url, { org_id: orgId });
  invalidateMeCache();
  if (data?.org?.id) await Token_Handler.setOrgId(data.org.id);
  if (data?.data?.org?.id) await Token_Handler.setOrgId(data.data.org.id);
  return data;
};
